/* global Word */

import type { LayoutConfig, TemplateProfile } from "../../../templates/types";
import type { ExhibitInsertionStrategy } from "../types";
import { TableBuilder } from "../../builders/TableBuilder";
import { setupHeaderCell, setupGraphCell, setupSourceCell } from "../cellSetup";

/**
 * Inserts a single half-width exhibit table (3 rows × 1 column).
 */
export class HalfStrategy implements ExhibitInsertionStrategy {
  readonly layoutType = "half" as const;

  async execute(
    context: Word.RequestContext,
    anchorRange: Word.Range,
    config: LayoutConfig,
    profile: TemplateProfile,
    startingExhibitNumber: number
  ): Promise<Word.Table> {
    const sepWidth = config.sepWidth ?? 11; // fallback 11pt
    const totalWidth = config.colWidth * 2 + sepWidth;

    // ── 1. Insert 3×3 table shell ────────────────────────────────────────────
    const table = anchorRange.insertTable(3, 3, Word.InsertLocation.after);
    TableBuilder.clearAllBorders(table);
    TableBuilder.lockWidth(table, totalWidth);

    // ── 2. FIRST row load+sync — for height setting ──────────────────────────
    table.rows.load("items");
    await context.sync();

    const rowsForHeight = table.rows.items;
    rowsForHeight[0].setHeight(config.row1Height, Word.RowHeightRule.atLeast);
    rowsForHeight[1].setHeight(config.row2Height, Word.RowHeightRule.atLeast);
    rowsForHeight[2].setHeight(config.row3Height, Word.RowHeightRule.atLeast);

    // ── 3. SECOND row load+sync — mandatory after height mutation ────────────
    table.rows.load("items");
    await context.sync();

    const rows = table.rows.items;

    // ── 4. Load ALL cells across all three rows in one batch ─────────────────
    rows[0].cells.load("items");
    rows[1].cells.load("items");
    rows[2].cells.load("items");
    await context.sync();

    // ── 5. Zero-pad separator cells (column 1) and right cells (column 2) ─────
    for (let rowIndex = 0; rowIndex < 3; rowIndex++) {
      TableBuilder.zeroCellPadding(rows[rowIndex].cells.items[1]);
      TableBuilder.clearCellBorders(rows[rowIndex].cells.items[1]);
      TableBuilder.zeroCellPadding(rows[rowIndex].cells.items[2]);
      TableBuilder.clearCellBorders(rows[rowIndex].cells.items[2]);
    }

    // ── 6. Setup left graph column (col 0) ───────────────────────────────────
    await setupHeaderCell(context, rows[0].cells.items[0], startingExhibitNumber, profile, { leftIndent: 2 });
    await setupGraphCell(context, rows[1].cells.items[0], { height: config.row2Height, width: config.colWidth });
    await setupSourceCell(context, rows[2].cells.items[0], profile, { leftIndent: 2 });

    // ── 7. Lock cell widths after all content is populated to prevent Word reflow ──
    for (let rowIndex = 0; rowIndex < 3; rowIndex++) {
      TableBuilder.lockColumnWidth(rows[rowIndex].cells.items[0], config.colWidth);
      TableBuilder.lockColumnWidth(rows[rowIndex].cells.items[1], sepWidth);
      TableBuilder.lockColumnWidth(rows[rowIndex].cells.items[2], config.colWidth);
    }

    return table;
  }
}